/**
 * Shared card type / status catalogs (Storybook + prop validators).
 *
 * ER-11336 — the legacy `CARD_TYPES` × `STATUS_BY_TYPE` matrix is kept for the
 * coach card and Storybook history, but the employee card no longer consumes it:
 * it now resolves facets (`media` / `hasQuiz` / `hasInspection` / `notApplicable`)
 * and a single normalised status enum (`CARD_STATUSES`).
 */

export const CARD_TYPES = [
  "Video + Quiz + Inspection",
  "Article + Quiz + Inspection",
  "Video + Quiz",
  "Article + Quiz",
  "Video + Inspection",
  "Article + Inspection",
  "Inspector-only",
];

export const STATUS_BY_TYPE = {
  "Video + Quiz + Inspection": [
    "Not started",
    "Quiz not attempted",
    "Quiz passed / inspection pending",
    "Quiz failed",
    "Complete",
    "Inspection failed",
    "Obsolete",
  ],
  "Article + Quiz + Inspection": [
    "Not started",
    "Quiz not attempted",
    "Quiz passed / inspection pending",
    "Quiz failed",
    "Complete",
    "Inspection failed",
    "Obsolete",
  ],
  "Video + Quiz": ["Not started", "Quiz not attempted", "Quiz failed", "Complete"],
  "Article + Quiz": ["Not started", "Quiz failed", "Complete"],
  "Video + Inspection": [
    "Not started",
    "Inspection pending",
    "Inspection passed",
    "Not Applicable",
    "Inspection failed",
  ],
  "Article + Inspection": [
    "Not started",
    "Inspection pending",
    "Inspection passed",
    "Not Applicable",
    "Inspection failed",
  ],
  "Inspector-only": ["Awaiting inspection", "Inspection passed", "Inspection failed"],
};

export const CHIP_VARIANTS = ["success", "neutral", "warning", "error", "inspector"];

/**
 * The single normalised card-status enum (ER-11336 Step 2). Every shape shares
 * these values; legacy human-readable strings translate through
 * `CARD_STATUS_LEGACY_MAP`.
 */
export const CARD_STATUSES = [
  "not_started",
  "quiz_not_attempted",
  "quiz_failed",
  "inspection_ready",
  "inspection_failed",
  "passed",
  "reset",
  "not_applicable",
];

/**
 * Old status vocabulary → new enum. Keys include both the code spellings
 * (`resolveCardStatus` in buildCheckpointInspectionLevels) and the Figma
 * spellings, so callers can migrate incrementally and Storybook keeps its
 * history. Lookup is case-insensitive (see `normalizeCardStatus`).
 */
export const CARD_STATUS_LEGACY_MAP = {
  "Not started": "not_started",
  "Quiz not attempted": "quiz_not_attempted",
  "Quiz failed": "quiz_failed",
  "Quiz passed / inspection pending": "inspection_ready",
  "Inspection pending": "inspection_ready",
  "Inspection ready": "inspection_ready",
  "Awaiting inspection": "inspection_ready",
  "Inspection failed": "inspection_failed",
  Complete: "passed",
  Passed: "passed",
  "Inspection passed": "passed",
  Reset: "reset",
  Obsolete: "reset",
  "Not Applicable": "not_applicable",
};

/** Canonical display label for each normalised status (used for the status indicator). */
export const CARD_STATUS_LABELS = {
  not_started: "Not started",
  quiz_not_attempted: "Quiz not attempted",
  quiz_failed: "Quiz failed",
  inspection_ready: "Inspection ready",
  inspection_failed: "Inspection failed",
  passed: "Passed",
  reset: "Reset",
  not_applicable: "Not Applicable",
};

const LEGACY_BY_LOWER = Object.keys(CARD_STATUS_LEGACY_MAP).reduce((acc, label) => {
  acc[label.toLowerCase()] = CARD_STATUS_LEGACY_MAP[label];
  return acc;
}, {});

/**
 * Normalise any status spelling — the new enum value, a legacy code label
 * ("Complete", "Obsolete", …) or a Figma label ("Inspection ready", …) — to a
 * `CARD_STATUSES` value. Returns null for an unrecognised status so callers can
 * warn in dev instead of rendering silently.
 */
export function normalizeCardStatus(status) {
  if (status == null) return null;
  const trimmed = String(status).trim();
  if (CARD_STATUSES.includes(trimmed)) return trimmed;
  return LEGACY_BY_LOWER[trimmed.toLowerCase()] || null;
}

/**
 * Decompose the legacy flattened type string ("Video + Quiz + Inspection",
 * "Inspector-only", "Not Applicable") into the three real facets.
 * Used only when facet props are not supplied — the card is driven by the
 * facet props when those are set (ER-11336 Step 1).
 */
export function resolveCardTypeFacets(type) {
  const trimmed = String(type || "").trim();
  const lower = trimmed.toLowerCase();

  if (!trimmed || lower === "not applicable") {
    return {
      media: "none",
      hasQuiz: false,
      hasInspection: false,
      isInspectorOnly: false,
      notApplicable: lower === "not applicable",
    };
  }

  const media = lower.includes("video") ? "video" : lower.includes("article") ? "article" : "none";
  const hasQuiz = lower.includes("quiz");
  const hasInspection = lower.includes("inspection");
  const isInspectorOnly =
    trimmed === "Inspector-only" || (!hasQuiz && !hasInspection && media === "none");

  return { media, hasQuiz, hasInspection, isInspectorOnly, notApplicable: false };
}

export { RAIL_BY_STATUS, RAIL_COLOR_BY_TONE } from "./asEmployeeCheckpointInspectionCardRails";
